<template>
  <div class="feedback-board flex flex-col h-full overflow-hidden">
    <!-- Columns -->
    <div class="flex gap-4 flex-1 overflow-x-auto px-6 py-4 min-h-0">
      <div
        v-for="(col, colIndex) in columns"
        :key="col.status"
        class="feedback-board-column flex flex-col min-w-[260px] flex-1 rounded-lg bg-gray-100 border border-gray-200"
        :class="{
          'ring-2 ring-indigo-400 bg-indigo-50/50': dragOverColumn === col.status,
          'opacity-50': dragOverColumn && !canDropTo(dragSourceStatus!, col.status),
        }"
        @dragover.prevent="onDragOver(col.status, $event)"
        @dragleave="onDragLeave(col.status)"
        @drop.prevent="onDrop(col.status)"
      >
        <!-- Column header -->
        <div class="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200">
          <span
            class="w-2.5 h-2.5 rounded-full flex-shrink-0"
            :class="col.dotColor"
          ></span>
          <span class="text-sm font-semibold text-gray-700">{{ col.label }}</span>
          <span class="text-xs text-gray-400 ml-auto">
            {{ columnItems(col.status).length }}
          </span>
        </div>

        <!-- Cards -->
        <div class="flex-1 overflow-y-auto p-2 flex flex-col gap-2 min-h-0">
          <div
            v-for="item in columnItems(col.status)"
            :key="item.id"
            draggable="true"
            @dragstart="onDragStart(item, col.status)"
            @dragend="onDragEnd"
            class="feedback-board-card bg-white rounded-md border border-gray-200 px-3 py-2.5 cursor-grab active:cursor-grabbing hover:border-indigo-300 hover:shadow-sm transition-all select-none"
            :class="{ 'opacity-40': draggingItemId === item.id }"
            @click="uiStore.viewFeedbackDetail(item.id)"
          >
            <div class="flex items-start gap-2">
              <!-- Priority -->
              <span
                class="inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold text-white flex-shrink-0"
                :class="getPriorityColor(item.priorityScore)"
              >
                {{ item.priorityScore }}
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-xs text-gray-900 leading-snug line-clamp-2">
                  {{ item.aiSummary || truncate(item.transcript, 80) }}
                </p>
                <div class="flex items-center gap-2 pt-1">
                  <span class="text-[10px] text-gray-400 truncate">{{ item.userEmail }}</span>
                  <span
                    v-if="item.type"
                    class="text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0"
                    :class="getTypeClass(item.type)"
                  >
                    {{ formatType(item.type) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2 pt-1.5">
              <span
                v-if="item.sentiment"
                class="text-[10px]"
                :class="{
                  'text-red-500': item.sentiment === 'frustrated',
                  'text-gray-400': item.sentiment === 'neutral',
                  'text-green-500': item.sentiment === 'positive',
                }"
              >
                {{ getSentimentIcon(item.sentiment) }}
              </span>
              <span class="text-[10px] text-gray-400 ml-auto">{{ formatDate(item.createdAt) }}</span>
            </div>
          </div>

          <p
            v-if="columnItems(col.status).length === 0"
            class="text-xs text-gray-400 text-center py-6"
          >
            No items
          </p>
        </div>
      </div>
    </div>

    <!-- Drop rejection toast -->
    <Transition name="toast">
      <div
        v-if="dropRejectionMessage"
        class="fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-600 text-white text-sm rounded-lg shadow-lg z-50"
      >
        {{ dropRejectionMessage }}
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import apiClient from '../../api/client'
import { useUIStore } from '../../stores/uiStore'

const uiStore = useUIStore()

const props = defineProps<{
  items: any[]
}>()

const emit = defineEmits<{
  (e: 'statusChanged'): void
}>()

// Column definitions in pipeline order
const columns = [
  { status: 'new', label: 'New', dotColor: 'bg-blue-500' },
  { status: 'investigating', label: 'Investigating', dotColor: 'bg-yellow-500' },
  { status: 'planned', label: 'Planned', dotColor: 'bg-purple-500' },
  { status: 'in_progress', label: 'In Progress', dotColor: 'bg-indigo-500' },
  { status: 'resolved', label: 'Resolved', dotColor: 'bg-green-500' },
  { status: 'archived', label: 'Archived', dotColor: 'bg-gray-400' },
]

const statusOrder = columns.map(c => c.status)

// Drag state
const draggingItemId = ref<number | null>(null)
const dragSourceStatus = ref<string | null>(null)
const dragOverColumn = ref<string | null>(null)
const dropRejectionMessage = ref<string | null>(null)
let rejectionTimeout: ReturnType<typeof setTimeout> | null = null

function columnItems(status: string) {
  return (props.items || []).filter((item: any) => item.status === status)
}

function canDropTo(fromStatus: string, toStatus: string): boolean {
  if (fromStatus === toStatus) return false
  const fromIdx = statusOrder.indexOf(fromStatus)
  const toIdx = statusOrder.indexOf(toStatus)
  // Can only move to adjacent column (one step forward or back)
  return Math.abs(toIdx - fromIdx) === 1
}

function onDragStart(item: any, status: string) {
  draggingItemId.value = item.id
  dragSourceStatus.value = status
}

function onDragOver(status: string, event: DragEvent) {
  if (!dragSourceStatus.value) return
  if (canDropTo(dragSourceStatus.value, status)) {
    dragOverColumn.value = status
  }
}

function onDragLeave(status: string) {
  if (dragOverColumn.value === status) {
    dragOverColumn.value = null
  }
}

function onDragEnd() {
  draggingItemId.value = null
  dragSourceStatus.value = null
  dragOverColumn.value = null
}

async function onDrop(toStatus: string) {
  dragOverColumn.value = null
  if (!draggingItemId.value || !dragSourceStatus.value) return

  if (!canDropTo(dragSourceStatus.value, toStatus)) {
    showRejection(`Can only move one step at a time`)
    onDragEnd()
    return
  }

  const itemId = draggingItemId.value
  onDragEnd()

  try {
    await apiClient.feedback.updateStatus(itemId, {
      status: toStatus,
      actorUserId: 'admin-001',
      actorName: 'Admin',
    })
    emit('statusChanged')
    uiStore.showNotification(`Moved to ${toStatus.replace(/_/g, ' ')}`, 'success')
  } catch (err: any) {
    uiStore.showNotification('Failed to update status: ' + (err.message || 'Unknown error'), 'error')
  }
}

function showRejection(msg: string) {
  if (rejectionTimeout) clearTimeout(rejectionTimeout)
  dropRejectionMessage.value = msg
  rejectionTimeout = setTimeout(() => {
    dropRejectionMessage.value = null
  }, 2000)
}

function getPriorityColor(score: number): string {
  if (score >= 80) return 'bg-red-600'
  if (score >= 60) return 'bg-orange-500'
  if (score >= 40) return 'bg-yellow-500'
  if (score >= 20) return 'bg-blue-500'
  return 'bg-gray-400'
}

function getTypeClass(type: string): string {
  const classes: Record<string, string> = {
    bug: 'bg-red-100 text-red-700',
    feature_request: 'bg-indigo-100 text-indigo-700',
    improvement: 'bg-teal-100 text-teal-700',
    support: 'bg-amber-100 text-amber-700',
    praise: 'bg-green-100 text-green-700',
    confusion: 'bg-orange-100 text-orange-700',
  }
  return classes[type] || 'bg-gray-100 text-gray-600'
}

function formatType(type: string): string {
  return type.replace(/_/g, ' ')
}

function getSentimentIcon(sentiment: string): string {
  if (sentiment === 'frustrated') return 'Frustrated'
  if (sentiment === 'positive') return 'Positive'
  return 'Neutral'
}

function truncate(text: string, maxLength: number): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / 3600000)
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
